import { createTheme, darken, lighten, TypeBackground } from '@mui/material/styles'
import type {} from '@mui/material/themeCssVarsAugmentation'
import type {} from '@mui/x-data-grid/themeAugmentation'
import type {} from '@mui/x-charts/themeAugmentation'
import type {} from '@mui/x-date-pickers/AdapterDateFns'
import { toggleButtonClasses, inputBaseClasses, chipClasses, selectClasses } from '@mui/material'
import { colors, AvailablePropsColorOverrides } from './theme/colors'

export const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data',
  },
  colorSchemes: {
    light: {
      palette: {
        mode: 'light',
        primary: {
          main: '#0ea5e9',
        },
        secondary: {
          main: '#6b7280',
        },
        background: {
          default: '#f9fafb',
          subtle: darken('#f9fafb', 0.033),
          paper: '#ffffff',
        },
        ...colors.light,
      },
    },
    dark: {
      palette: {
        mode: 'dark',
        primary: {
          main: '#0ea5e9',
        },
        secondary: {
          main: '#6b7280',
        },
        background: {
          default: '#0f172a',
          subtle: lighten('#0f172a', 0.033),
          paper: '#1e293b',
        },
        ...colors.dark,
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 840,
      lg: 1024,
      xl: 1200,
      '2xl': 1536,
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  sizes: {
    element: {
      // Based on button size = font-size * line-height + padding-vertical * 2 - border-width * 2
      small: 30.75, // fs = 0.8125rem, lh = 1.75, py = 4px
      medium: 36.5, // fs = 0.875rem, lh = 1.75, py = 6px
      large: 42.25, // fs = 0.9375rem, lh = 1.75, py = 8px
    },
    sidebar: {
      xs: 0,
      sm: 0,
      md: 0,
      lg: 240,
      xl: 240,
      '2xl': 240,
      current: 'var(--mui-sizes-sidebar-current)',
    },
    sidebarCollapsed: {
      xs: 0,
      sm: 0,
      md: 0,
      lg: 56,
      xl: 56,
      '2xl': 56,
      current: 'var(--mui-sizes-sidebar-collapsed-current)',
    },
    appTopBar: 80,
    index: {
      spacing: {
        xs: 2,
        md: 3,
        lg: 3,
        xl: 4,
        current: 'var(--mui-sizes-index-spacing-current)',
      },
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          variants: buttonSizeVariants(),
        },
      },
    },
    MuiToggleButtonGroup: {
      defaultProps: {
        stableHeight: true,
      },
      styleOverrides: {
        grouped: [
          {
            variants: [
              ...buttonSizeVariants(),
              {
                props: { variant: 'text' },
                style: ({ theme }) => [
                  {
                    border: 'none',
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: 'transparent',
                  },
                  theme.unstable_sx({
                    [`&.${toggleButtonClasses.selected}`]: {
                      backgroundColor: 'transparent',
                    },
                  }),
                ],
              },
            ],
          },
        ],
      },
    },
    MuiToggleButton: {
      defaultProps: {
        stableHeight: true,
      },
      styleOverrides: {
        root: ({ theme }) => [
          {
            color: theme.vars.palette.secondary.light,
          },
          theme.applyStyles('dark', {
            color: theme.vars.palette.secondary.main,
          }),
          {
            variants: [
              ...buttonSizeVariants(),
              {
                props: { variant: 'text' },
                style: ({ theme }) => [
                  {
                    border: 'none',
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: 'transparent',
                  },
                  theme.unstable_sx({
                    [`&.${toggleButtonClasses.selected}`]: {
                      backgroundColor: 'transparent',
                    },
                  }),
                ],
              },
            ],
          },
        ],
      },
    },

    MuiInputBase: {
      variants: [
        {
          props: { size: 'smaller' },
          style: [
            {
              [`& .${inputBaseClasses.root}`]: {
                fontSize: '0.9rem',
              },
              [`& .${inputBaseClasses.input}`]: {
                padding: '7px 14px',
              },
            },
          ],
        },
      ],
    },
    MuiTextField: {
      variants: [
        {
          props: { size: 'smaller' },
          style: [
            {
              [`& .${inputBaseClasses.root}`]: {
                fontSize: '0.9rem',
              },
              [`& .${inputBaseClasses.input}`]: {
                padding: '7px 14px',
              },
            },
          ],
        },
      ],
    },
    MuiSelect: {
      variants: [
        {
          props: { size: 'smaller' },
          style: [
            {
              [`& .${inputBaseClasses.root}`]: {
                fontSize: '0.9rem',
              },
              [`& .${inputBaseClasses.input}`]: {
                padding: '6px 14px',
              },
            },
          ],
        },
      ],
    },
    MuiAutocomplete: {
      variants: [
        {
          props: { size: 'smaller' },
          style: [
            {
              [`& .${inputBaseClasses.root}`]: {
                fontSize: '0.9rem',
                padding: '5px 10px !important',
              },
              [`& .${inputBaseClasses.input}`]: {
                padding: '2px 4px !important',
              },
              [`& .${chipClasses.root}`]: {
                height: 'calc(1.4375rem - 2px)',
                marginTop: '0px !important',
                marginBottom: '3px !important',
                fontSize: '0.75rem',
                position: 'relative',
                top: '2px',
              },
              [`& .${chipClasses.label}`]: {
                paddingRight: '7px',
                paddingLeft: '6px',
              },
            },
          ],
        },
      ],
    },

    MuiDrawer: {
      styleOverrides: {
        root: {
          transition: 'width 240ms',
          overflow: 'hidden',
        },
        paper: [
          {
            backgroundColor: '#1e293b',
            color: '#cbd5e1',
            borderColor: 'transparent',
            transition: 'width 240ms',
            overflow: 'hidden',
          },
          ({ theme }) =>
            theme.applyStyles('dark', {
              '--Paper-overlay': 'var(--mui-overlays-0) !important',
              backgroundColor: '#0f172a',
              borderColor: theme.vars.palette.divider,
            }),
        ],
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: [
          ({ theme }) => ({
            height: theme.sizes.appTopBar,
            backgroundColor: theme.vars.palette.background.paper,
            color: 'rgba(0, 0, 0, 0.87)',
          }),
          ({ theme }) =>
            theme.applyStyles('dark', {
              color: '#cbd5e1',
            }),
        ],
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: theme.sizes.appTopBar,
        }),
      },
    },
  },
})

export const responsiveVars = [
  // Add any other responsive vars here
  theme.vars.sizes.sidebar,
  theme.vars.sizes.sidebarCollapsed,
  theme.vars.sizes.index.spacing,
]

function buttonSizeVariants(): any {
  return [
    {
      props: { rectangle: true, size: 'small' },
      style: ({ theme }: any) => ({
        width: theme.vars.sizes.element.small,
        height: theme.vars.sizes.element.small,
        minWidth: 'unset !important',
      }),
    },
    {
      props: { rectangle: true, size: 'medium' },
      style: ({ theme }: any) => ({
        width: theme.vars.sizes.element.medium,
        height: theme.vars.sizes.element.medium,
        minWidth: 'unset !important',
      }),
    },
    {
      props: { rectangle: true, size: 'large' },
      style: ({ theme }: any) => ({
        width: theme.vars.sizes.element.large,
        height: theme.vars.sizes.element.large,
        minWidth: 'unset !important',
      }),
    },
    {
      props: { stableHeight: true, size: 'small' },
      style: ({ theme }: any) => ({
        height: theme.vars.sizes.element.small,
        padding: '4px 5px',
      }),
    },
    {
      props: { stableHeight: true, size: 'medium' },
      style: ({ theme }: any) => ({
        height: theme.vars.sizes.element.medium,
        padding: '4px 8px',
      }),
    },
    {
      props: { stableHeight: true, size: 'large' },
      style: ({ theme }: any) => ({
        height: theme.vars.sizes.element.large,
        padding: '8px 11px',
      }),
    },
  ]
}

declare module '@mui/material/styles' {
  interface Palette {
    background: TypeBackground
  }

  interface Theme {
    palette: Palette
    sizes: {
      element: { small: 30.75; medium: 36.5; large: 42.25 }
      sidebar: { xs: 0; sm: 0; md: 0; lg: 240; xl: 240; '2xl': 240; current: string }
      sidebarCollapsed: { xs: 0; sm: 0; md: 0; lg: 56; xl: 56; '2xl': 56; current: string }
      appTopBar: 80
      index: {
        spacing: { xs: 2; md: 3; lg: 3; xl: 4; current: string }
      }
    }
  }

  interface ThemeOptions {
    sizes?: Partial<Theme['sizes']>
  }

  interface ThemeVars {
    sizes: Theme['sizes']
  }

  interface BreakpointOverrides {
    xs: true
    sm: true
    md: true
    lg: true
    xl: true
    '2xl': true
  }

  interface TypeBackground {
    subtle: string
  }
}

declare module '@mui/material/Button' {
  interface ButtonOwnProps {
    rectangle?: boolean
    stableHeight?: boolean
  }
}

declare module '@mui/material/ToggleButtonGroup' {
  interface ToggleButtonGroupProps {
    variant?: 'text' | null
    rectangle?: boolean
    stableHeight?: boolean
  }

  interface ToggleButtonGroupPropsVariantOverrides {
    text: true
  }
}

declare module '@mui/material/ToggleButton' {
  interface ToggleButtonOwnProps {
    variant?: 'text' | null
    rectangle?: boolean
    stableHeight?: boolean
  }

  interface ToggleButtonPropsVariantOverrides {
    text: true
  }
}

declare module '@mui/material/InputBase' {
  interface InputBasePropsSizeOverrides {
    smaller: true
  }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    smaller: true
  }
}

declare module '@mui/material/Autocomplete' {
  interface AutocompletePropsSizeOverrides {
    smaller: true
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides extends AvailablePropsColorOverrides {}
}
